import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Camera, Star, MessageSquare, Trophy, ShoppingBag, Heart, Vote, Users, Gift, Sparkles, Lock, Target, Crown, FlaskConical } from "lucide-react";
import { API, inr } from "../lib/api";
import ProductCard from "../components/ProductCard";
import DailyVibe from "../components/DailyVibe";
import ChallengeBanner from "../components/ChallengeBanner";
import { DiscoverHerWorld, RupeeJourney, HonestPrices, MadeForHer, ShopByOccasion } from "../components/HomeEditorial";
import Seo from "../components/Seo";
import { organizationLd, websiteLd } from "../lib/seo";

const CREATE_OPTS = [
  ["Post a Look", "/share-look", Camera],
  ["Review a Product", "/share-look", Star],
  ["Ask Sakhis", "/ask-sakhis", MessageSquare],
  ["Join a Challenge", "/suvikka-sakhi", Trophy],
];

const STORY_ICONS = { Community: Users, Challenges: Trophy, "Secret Lab": FlaskConical, Club: Crown, Creators: Star };

function LookCard({ post }) {
  return (
    <div className="break-inside-avoid mb-5 group" data-testid={`home-look-${post.id}`}>
      <div className="relative overflow-hidden rounded-2xl bg-stone-100">
        {post.media?.[0] && (
          <img src={post.media[0]} alt={post.caption || post.user_name} loading="lazy"
            className="w-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-transparent" />
        {post.products?.length > 0 && (
          <span className="absolute top-3 left-3 bg-white/95 text-stone-900 text-[10px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1 shadow-sm">
            <ShoppingBag className="w-3 h-3" /> Shop the look
          </span>
        )}
        <div className="absolute inset-x-0 bottom-0 p-3.5 text-white">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-[#6A1B29] text-white grid place-items-center text-xs font-semibold shrink-0">{post.user_name?.[0] || "S"}</span>
            <p className="text-xs font-semibold truncate">{post.user_name}</p>
          </div>
          {post.caption && <p className="text-[13px] leading-snug mt-1.5 line-clamp-2 text-white/95">{post.caption}</p>}
        </div>
      </div>
      {post.products?.length > 0 && (
        <div className="flex gap-2 mt-2.5 overflow-x-auto scrollbar-hide" data-testid={`home-look-shop-${post.id}`}>
          {post.products.slice(0, 3).map((pr) => (
            <Link key={pr.id} to={`/product/${pr.slug || pr.id}`} data-testid={`home-shop-item-${pr.id}`}
              className="shrink-0 flex items-center gap-2 bg-white border border-stone-200 rounded-full pl-1.5 pr-3 py-1.5 hover:border-[#6A1B29] transition-colors">
              <img src={pr.image} alt="" className="w-7 h-7 rounded-full object-cover" />
              <span className="text-[11px] font-semibold text-stone-800 whitespace-nowrap">{inr(pr.selling_price)}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [trending, setTrending] = useState([]);
  const [drops, setDrops] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [looks, setLooks] = useState(null);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    API.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    API.get("/products?trending=true&limit=8").then((r) => setTrending(r.data.items)).catch(() => {});
    API.get("/drops").then((r) => setDrops(r.data)).catch(() => {});
    API.get("/products?limit=50").then((r) => setCatalogue(r.data.items)).catch(() => {});
    API.get("/community/posts?limit=12").then((r) => setLooks(r.data)).catch(() => setLooks([]));
    API.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const HERO = settings.home_hero_image || "https://images.unsplash.com/photo-1727784891995-e311b1b6ba18?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
  const catImg = (s) => categories.find((c) => c.slug === s)?.image || "";
  const DEFAULT_STORIES = [
    ["Beauty", "/category/cosmetics", catImg("cosmetics")],
    ["Jewellery", "/category/jewellery", catImg("jewellery")],
    ["Fashion", "/category/ladies-fashion", catImg("ladies-fashion")],
    ["Bags", "/category/bags", catImg("bags")],
    ["Community", "/sakhi-wall", ""],
    ["Challenges", "/suvikka-sakhi", ""],
    ["Secret Lab", "/secret-lab/project-cherry", ""],
    ["Club", "/club", ""],
    ["Creators", "/creators", ""],
  ];
  const STORIES = (settings.home_stories && settings.home_stories.length)
    ? settings.home_stories.map((x) => [x.label, x.to, x.img || ""])
    : DEFAULT_STORIES;
  const beauty = catalogue.filter((p) => ["Cosmetics", "Skincare", "Intimates"].includes(p.category)).slice(0, 4);
  const style = catalogue.filter((p) => !["Cosmetics", "Skincare"].includes(p.category)).slice(0, 4);

  return (
    <div>
      <Seo
        title="Suvikka Sakhi | Women's Fashion, Beauty & Lifestyle Community"
        description="Suvikka Sakhi — a community for women to connect, create and grow together. Discover beauty, fashion and jewellery, share your looks, join the Club and shop what the Sakhis love."
        canonicalPath="/"
        jsonLd={[organizationLd, websiteLd]}
      />

      {/* HERO — full-bleed editorial */}
      <section className="relative" data-testid="hero-section">
        <div className="relative min-h-[560px] sm:min-h-[640px] overflow-hidden bg-[#2A1418]">
          <img src={HERO} alt="Woman in gold jewellery — the Suvikka muse" className="absolute inset-0 w-full h-full object-cover object-[70%_center]" data-testid="hero-image" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E0B10]/90 via-[#2A1418]/55 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#1E0B10]/60 to-transparent" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center min-h-[560px] sm:min-h-[640px]">
            <div className="py-20 max-w-2xl text-white reveal">
              <p className="text-xs sm:text-sm font-semibold tracking-[0.35em] uppercase text-[#F0C97B]">{settings.home_hero_eyebrow || "India's Women-First Community"}</p>
              <h1 className="font-display text-5xl sm:text-7xl leading-[1.02] font-bold mt-5" data-testid="hero-headline">
                {settings.home_hero_title1 || "Everything"}<br /><span className="italic text-[#F6C9B8]">{settings.home_hero_title2 || "She Loves."}</span>
              </h1>
              <p className="text-base sm:text-lg text-white/85 mt-5 max-w-lg">
                {settings.home_hero_subtitle || "Beauty, fashion, jewellery, lifestyle and a community made for her. Discover, share and shop what the Sakhis love."}
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/products" data-testid="hero-explore" className="inline-flex items-center gap-2 bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-7 py-3.5 rounded-full transition-transform hover:scale-105">Explore Suvikka <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/suvikka-sakhi" data-testid="hero-join-club" className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/30 hover:bg-white hover:text-[#2A1418] text-white font-semibold px-7 py-3.5 rounded-full transition-colors">Join Suvikka Sakhi</Link>
                <Link to="/share-look" data-testid="hero-post-look" className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/30 hover:bg-white hover:text-[#2A1418] text-white font-semibold px-6 py-3.5 rounded-full transition-colors"><Camera className="w-4 h-4" /> Share Your Look</Link>
              </div>
              <p className="mt-7 inline-flex items-center gap-2 text-sm text-white/70"><span className="w-1.5 h-1.5 rounded-full bg-[#F0C97B]" /> Share • Discover • Belong</p>
            </div>
          </div>
        </div>
        {/* STORY CIRCLES overlapping */}
        <div className="bg-[#FFFDF7] border-b border-[#EADBD8]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative">
            <div className="flex gap-4 sm:gap-6 overflow-x-auto pb-5 scrollbar-hide" data-testid="story-row">
              {STORIES.map(([label, to, img], i) => {
                const StoryIcon = STORY_ICONS[label] || Sparkles;
                return (
                  <Link key={i} to={to} data-testid={`story-${i}`} className="shrink-0 flex flex-col items-center gap-2 w-[72px]">
                    <span className="w-[68px] h-[68px] rounded-full p-[3px] bg-gradient-to-tr from-[#6A1B29] to-[#D4AF37]">
                      <span className="w-full h-full rounded-full bg-white grid place-items-center overflow-hidden">
                        {img ? <img src={img} alt={label} className="w-full h-full object-cover rounded-full" /> : <StoryIcon className="w-6 h-6 text-[#6A1B29]" />}
                      </span>
                    </span>
                    <span className="text-[11px] font-medium text-[#231518] text-center leading-tight">{label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* RIGHT NOW strip */}
      <section className="bg-[#2A1418] text-white" data-testid="live-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3 overflow-x-auto scrollbar-hide">
          <span className="text-[11px] font-bold uppercase tracking-widest text-[#F6A5BE] shrink-0">Right Now</span>
          {[[Heart, "Sakhi Wall", "/sakhi-wall"], [Camera, "Post a Look", "/share-look"], [Vote, "Vote — Project Cherry", "/secret-lab/project-cherry"], [Trophy, "Challenges", "/suvikka-sakhi"], [MessageSquare, "Ask Sakhis", "/ask-sakhis"]].map(([Icon, t, to], i) => (
            <Link key={i} to={to} data-testid={`live-chip-${i}`} className="shrink-0 inline-flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/20 rounded-full px-4 py-2 transition-colors whitespace-nowrap"><Icon className="w-4 h-4" /> {t}</Link>
          ))}
        </div>
      </section>

      {/* DAILY VIBE — daily return ritual */}
      <DailyVibe />
      <section className="py-14 sm:py-20 bg-[#FAF6F1]" data-testid="home-sakhi-feed">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29] flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#6A1B29] animate-pulse" /> Happening at Suvikka</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-900 mt-3">The Sakhis Are Styling.</h2>
              <p className="text-stone-500 mt-2 max-w-xl">Real looks from the community — tap any find to shop it.</p>
            </div>
            <Link to="/sakhi-wall" data-testid="feed-view-wall" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-stone-900 hover:text-[#6A1B29] whitespace-nowrap">See the Wall <ArrowRight className="w-4 h-4" /></Link>
          </div>

          {looks === null ? (
            <div className="columns-2 md:columns-4 gap-5 mt-8">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="mb-5 rounded-2xl bg-stone-200/70 animate-pulse" style={{ height: 200 + (i % 3) * 70 }} />)}</div>
          ) : looks.length === 0 ? (
            <div className="mt-8 bg-white border border-stone-200 rounded-3xl p-12 text-center" data-testid="feed-empty">
              <Heart className="w-8 h-8 text-[#6A1B29] mx-auto" />
              <p className="font-display text-xl text-stone-800 mt-3">Be the first Sakhi to post a look</p>
              <Link to="/share-look" className="inline-block mt-5 bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-6 py-3 rounded-full text-sm" data-testid="feed-empty-cta">Show us your look</Link>
            </div>
          ) : (
            <div className="columns-2 md:columns-4 gap-5 mt-8" data-testid="home-feed-grid">
              {looks.map((p) => <LookCard key={p.id} post={p} />)}
            </div>
          )}
        </div>
      </section>

      {/* CREATE band — aggressively invite creation */}
      <section className="py-14 sm:py-20 bg-[#2A1418] text-white" data-testid="home-create-band">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-2">
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#F6A5BE]">Your Turn, Sakhi</p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-3">Don't just scroll.<br /><span className="text-[#F6A5BE] italic">Create.</span></h2>
            <p className="text-stone-300 mt-3 max-w-md">Post your look, review a find, ask the Sakhis or join this week's challenge. Earn wallet rewards as you go.</p>
          </div>
          <div className="lg:col-span-3 grid grid-cols-2 gap-3 sm:gap-4">
            {CREATE_OPTS.map(([label, to, Icon]) => (
              <Link key={label} to={to} data-testid={`home-create-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className="group bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 rounded-2xl p-5 transition-colors">
                <span className="w-11 h-11 grid place-items-center rounded-xl bg-[#6A1B29] text-white"><Icon className="w-5 h-5" /></span>
                <p className="font-semibold mt-3.5 flex items-center gap-1.5">{label} <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" /></p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* LIVE CHALLENGE banner */}
      <ChallengeBanner />

      {/* SECRET LAB vote */}
      <section className="py-14 sm:py-20 bg-[#FDF5E6] border-y border-[#EADBD8]" data-testid="home-secret-lab">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#6A1B29]">Secret Lab · Voting Live</p>
          <h2 className="font-display text-3xl sm:text-5xl font-semibold text-[#231518] mt-3">You Decide What Suvikka Makes Next.</h2>
          <p className="text-[#755C62] mt-3">From shades to packaging — the girls vote, and Suvikka builds it. Have your say.</p>
          <Link to="/secret-lab/project-cherry" data-testid="home-vote-cta" className="inline-flex items-center gap-2 mt-7 bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-8 py-3.5 rounded-full transition-transform hover:scale-105"><Vote className="w-4 h-4" /> Cast Your Vote</Link>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-16 sm:py-24" id="categories" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Categories</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-900 mt-3">Everything She Loves. Under One Roof.</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-10">
            {["cosmetics", "jewellery", "bags", "ladies-fashion"]
              .map((s) => categories.find((c) => c.slug === s))
              .filter(Boolean)
              .map((c) => (
              <Link key={c.id} to={`/category/${c.slug}`}
                data-testid={`category-card-${c.slug}`} className="group relative overflow-hidden bg-stone-200 aspect-[4/5] sm:aspect-[3/4] rounded-2xl">
                {c.image && <img src={c.image} alt={c.name} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/85 via-stone-950/20 to-transparent" />
                <div className="absolute bottom-0 p-4 sm:p-6 w-full">
                  <h3 className="font-display text-xl sm:text-2xl font-semibold text-white">{c.slug === "ladies-fashion" ? "Fashion" : c.name}</h3>
                  <p className="text-xs sm:text-sm text-stone-300 mt-1 line-clamp-1">{c.description}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-white mt-3 border-b border-[#D4AF37] pb-0.5 group-hover:text-[#F0C97B] transition-colors">
                    EXPLORE <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* DISCOVER HER WORLD + ₹49 JOURNEY + HONEST PRICES */}
      <DiscoverHerWorld />
      <RupeeJourney />
      <HonestPrices />

      {/* TRENDING */}
      <section className="py-16 sm:py-24 bg-white border-y border-stone-200/70" data-testid="trending-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Bestsellers</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-900 mt-3">Trending Now</h2>
            </div>
            <Link to="/trending" data-testid="trending-view-all" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-stone-900 hover:text-[#6A1B29]">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 mt-10">
            {trending.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
          {trending.length === 0 && <p className="text-stone-500 mt-10" data-testid="trending-empty">Products coming soon.</p>}
        </div>
      </section>

      {/* MADE FOR HER */}
      <MadeForHer />

      {/* CLUB + WALLET visual bands */}
      <section className="py-14 sm:py-20 bg-[#FAF6F1]" data-testid="home-club-wallet">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="relative rounded-3xl overflow-hidden min-h-[340px] bg-gradient-to-br from-[#6A1B29] to-[#501325] text-white p-8 flex flex-col justify-center" data-testid="home-club-band">
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-white/80">Suvikka Club · Your Inner Circle</p>
            <h3 className="font-display text-3xl sm:text-4xl font-semibold mt-2">Everything She Loves,<br />More Exclusively.</h3>
            <p className="text-white/90 mt-3 max-w-md">Member prices, wallet rewards, early access to Club Drops and a seat in the Secret Lab — for one simple yearly membership.</p>
            <div className="flex flex-wrap gap-2.5 mt-5">
              {[[Heart, "Member Prices"], [Gift, "Wallet Rewards"], [Sparkles, "Early Access"], [Lock, "Club Drops"]].map(([Icon, label], i) => (
                <span key={i} className="inline-flex items-center gap-1.5 bg-white/15 border border-white/20 rounded-full px-3.5 py-1.5 text-xs font-semibold"><Icon className="w-3.5 h-3.5" /> {label}</span>
              ))}
            </div>
            <Link to="/club" data-testid="home-club-cta" className="mt-6 w-fit bg-white text-[#6A1B29] font-semibold px-7 py-3.5 rounded-full hover:scale-105 transition-transform">Join Suvikka Club</Link>
          </div>
          <div className="bg-white rounded-3xl border border-stone-200 p-8 flex flex-col justify-center" data-testid="home-wallet-band">
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Suvikka Wallet</p>
            <h3 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mt-2">Earn As You Create.</h3>
            <p className="text-stone-600 mt-3">Every look, review, referral and challenge can earn you wallet rewards — spend them on your next favourite find.</p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {[[Camera, "Post a Look"], [Star, "Review a Product"], [Users, "Refer a Sakhi"], [Target, "Join a Challenge"]].map(([Icon, label], i) => (
                <div key={i} className="flex items-center gap-2.5 bg-[#FFF7FA] rounded-xl px-3.5 py-3 text-sm font-medium text-stone-800"><Icon className="w-4 h-4 text-[#6A1B29] shrink-0" /> {label}</div>
              ))}
            </div>
            <Link to="/wallet" data-testid="view-wallet" className="inline-block mt-6 bg-stone-900 hover:bg-stone-800 text-white text-sm font-semibold px-6 py-3 rounded-full w-fit">View Wallet</Link>
          </div>
        </div>
      </section>

      {/* SHOP BY OCCASION */}
      <ShopByOccasion />

      {/* CLUB DROPS carousel */}
      {drops.length > 0 && (
        <section className="py-14 sm:py-20" data-testid="home-club-drops">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Club Drops</p>
                <h2 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 mt-2">Exclusive Finds. Just for You.</h2>
              </div>
              <Link to="/drops" data-testid="view-all-drops" className="text-sm font-semibold text-[#6A1B29] whitespace-nowrap">VIEW ALL <ArrowRight className="w-4 h-4 inline" /></Link>
            </div>
            <div className="flex gap-4 mt-8 overflow-x-auto pb-3 scrollbar-hide">
              {drops.map((p) => (
                <Link key={p.id} to={`/product/${p.slug || p.id}`} data-testid={`home-drop-${p.sku}`} className="shrink-0 w-48 sm:w-56 group">
                  <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-stone-100">
                    {p.images?.[0] && <img src={p.images[0]} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                    <span className="absolute top-3 left-3 bg-[#6A1B29] text-white text-[10px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Club Drop</span>
                  </div>
                  <h3 className="text-sm font-medium text-stone-900 mt-2 line-clamp-1">{p.title}</h3>
                  <p className="text-xs text-stone-400">{p.category}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BEAUTY ESSENTIALS */}
      {beauty.length > 0 && (
        <section className="py-16 sm:py-24" data-testid="beauty-essentials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Beauty</p>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-900 mt-3">Beauty Essentials</h2>
                <p className="text-stone-500 mt-3 max-w-xl">Cosmetics, skincare and everyday beauty picks — curated for the modern woman.</p>
              </div>
              <Link to="/category/cosmetics" data-testid="beauty-view-all" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-stone-900 hover:text-[#6A1B29]">
                Shop Beauty <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-10">
              {beauty.map((p, i) => <ProductCard key={p.id} product={p} index={`beauty-${i}`} />)}
            </div>
          </div>
        </section>
      )}

      {/* STYLE YOUR EVERYDAY */}
      {style.length > 0 && (
        <section className="py-16 sm:py-24 bg-[#FAF6F1]" data-testid="style-everyday-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Fashion & Accessories</p>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-900 mt-3">Style Your Everyday</h2>
                <p className="text-stone-500 mt-3 max-w-xl">Jewellery, bags, belts and accessories to complete every look.</p>
              </div>
              <Link to="/products" data-testid="style-view-all" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-stone-900 hover:text-[#6A1B29]">
                Shop Fashion <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-10">
              {style.map((p, i) => <ProductCard key={p.id} product={p} index={`style-${i}`} />)}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="py-16 sm:py-24 bg-[#2A1418] text-white" data-testid="home-final-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#F6A5BE]">Come Join the Girls of Suvikka</p>
          <h2 className="font-display text-3xl sm:text-5xl font-semibold mt-3">A Place to Discover, Belong &amp; Grow.</h2>
          <p className="text-stone-300 mt-4">Suvikka Sakhi is where women connect, create and celebrate each other. Your story starts here.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/share-look" data-testid="final-join-sakhi" className="bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-8 py-3.5 rounded-full transition-transform hover:scale-105">Post Your First Look</Link>
            <Link to="/products" data-testid="final-shop" className="border border-white/40 hover:bg-white hover:text-stone-900 text-white font-semibold px-8 py-3.5 rounded-full transition-colors">Shop Products</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
